import React, {useState, useEffect} from "react";
import {useSelector} from "react-redux";
import {CopySoreWrap} from "components/atoms/MenuManagement/CopyStore/CopyStore.styles";
import Form from "components/molecules/Form/Form";
import Field from "components/molecules/Field";
import {useForm} from "components/molecules/Form";
import Button from "components/molecules/Button";
import Toast from "components/molecules/Toast";
import manageStores from "services/manageSores";
import menuStoreService from "services/menu-storeService";
import arrowIcon from "../../../../assets/arrow-icon.svg";
import { useAuthContext } from "Context/authContext";

const CopyStore = ({onClose}) => {
    const [form] = useForm();
        const business = useSelector(state => state.business.id);
        const { user } = useAuthContext();
        const businessIdFromAuth = user?.businessId;
        const businessId = businessIdFromAuth || business;
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [loading, setLoading] = useState(false);
    const [storesLoading, setStoresLoading] = useState(true);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    
    // Data states
    const [stores, setStores] = useState([]);
    const [categories, setCategories] = useState([]);
    const [menuItems, setMenuItems] = useState({});
    const [selectedSourceStore, setSelectedSourceStore] = useState(null);
    const [selectedTargetStore, setSelectedTargetStore] = useState(null);
    
    // Form states
    const [copyState, setCopyState] = useState({
        entire: true,
        categories: false,
        items: false,
    });
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    
    // Fetch stores on component mount
    useEffect(() => {
        fetchStores();
    }, [businessId]);
    
    // Fetch categories when source store changes
    useEffect(() => {
        if (selectedSourceStore && (copyState.categories || copyState.items)) {
            fetchCategoriesAndItems();
        }
    }, [selectedSourceStore, copyState]);
    
    const fetchStores = async () => {
        try {
            setStoresLoading(true);
            const response = await manageStores.getStoresForCopy(businessId);
            const storeOptions = response.stores.map(store => ({
                label: store.name || store.title || store.storeName || 'Unnamed Store',
                value: store._id,
                data: store
            }));
            setStores(storeOptions);
        } catch (error) {
            console.error('Error fetching stores:', error);
            Toast({
                type: "error",
                message: "Failed to load stores. Please try again."
            });
        } finally {
            setStoresLoading(false);
        }
    };
    
    const fetchCategoriesAndItems = async () => {
        if (!selectedSourceStore) return;
        
        try {
            setCategoriesLoading(true);
            // Get menus for the selected store
            const menuResponse = await menuStoreService.getBranchMenu(selectedSourceStore);
            
            if (menuResponse?.menus && menuResponse.menus.length > 0) {
                // Get categories for the first menu (or you might want to handle multiple menus)
                const firstMenu = menuResponse.menus[0];
                const categoriesResponse = await menuStoreService.getAllcategories(firstMenu._id);
                
                if (categoriesResponse?.category) {
                    setCategories(categoriesResponse.category);
                    
                    // If items mode is selected, fetch items for each category
                    if (copyState.items) {
                        const itemsData = {};
                        for (const category of categoriesResponse.category) {
                            try {
                                const itemsResponse = await menuStoreService.getMenuItemsByCategory(category._id);
                                itemsData[category._id] = itemsResponse?.menuItems || [];
                            } catch (error) {
                                console.error(`Error fetching items for category ${category._id}:`, error);
                                itemsData[category._id] = [];
                            }
                        }
                        setMenuItems(itemsData);
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching categories and items:', error);
            Toast({
                type: "error",
                message: "Failed to load menu data. Please try again."
            });
        } finally {
            setCategoriesLoading(false);
        }
    };
    
    const handleSourceStoreChange = (value, option) => {
        // Extract the actual ID - handle both direct value and event object
        let actualValue;
        if (value?.target) {
            // It's an event object
            actualValue = value.target.value;
        } else if (typeof value === 'object' && value?.value) {
            // It's a store object, extract the ID
            actualValue = value.value;
        } else {
            // It's already a string ID
            actualValue = value;
        }
        setSelectedSourceStore(actualValue);
        // Reset selections when source store changes
        setSelectedCategories([]);
        setSelectedItems([]);
        setCategories([]);
        setMenuItems({});
    };
    
    const handleTargetStoreChange = (value, option) => {
        // Extract the actual ID - handle both direct value and event object
        let actualValue;
        if (value?.target) {
            // It's an event object
            actualValue = value.target.value;
        } else if (typeof value === 'object' && value?.value) {
            // It's a store object, extract the ID
            actualValue = value.value;
        } else {
            // It's already a string ID
            actualValue = value;
        }
        setSelectedTargetStore(actualValue);
    };
    
    const handleCopyModeChange = (mode) => {
        const newCopyState = {
            entire: mode === 'entire',
            categories: mode === 'categories',
            items: mode === 'items',
        };
        setCopyState(newCopyState);
        // Reset selections when mode changes
        setSelectedCategories([]);
        setSelectedItems([]);
    };
    
    const handleCategorySelection = (categoryId, checked) => {
        if (checked) {
            setSelectedCategories(prev => [...prev, categoryId]);
        } else {
            setSelectedCategories(prev => prev.filter(id => id !== categoryId));
        }
    };
    
    const handleItemSelection = (itemId, checked) => {
        if (checked) {
            setSelectedItems(prev => [...prev, itemId]);
        } else {
            setSelectedItems(prev => prev.filter(id => id !== itemId));
        }
    };
    
    const validateForm = () => {
        if (!selectedSourceStore) {
            Toast({
                type: "error",
                message: "Please select a source store"
            });
            return false;
        }
        
        if (!selectedTargetStore) {
            Toast({
                type: "error",
                message: "Please select a target store"
            });
            return false;
        }
        
        if (selectedSourceStore === selectedTargetStore) {
            Toast({
                type: "error",
                message: "Source and target stores cannot be the same"
            });
            return false;
        }
        
        if (copyState.categories && selectedCategories.length === 0) {
            Toast({
                type: "error",
                message: "Please select at least one category to copy"
            });
            return false;
        }
        
        if (copyState.items && selectedItems.length === 0) {
            Toast({
                type: "error",
                message: "Please select at least one item to copy"
            });
            return false;
        }
        
        return true;
    };
    
    const handleSubmit = async () => {
        if (!validateForm()) return;
        
        try {
            setLoading(true);
            
            // Prepare request payload based on copy mode
            // Extract IDs from store objects if needed
            const sourceId = selectedSourceStore?.value || selectedSourceStore;
            const targetId = selectedTargetStore?.value || selectedTargetStore;
            
            let payload = {
                sourceStoreId: sourceId,
                targetStoreIds: [targetId],
            };
            
            if (copyState.entire) {
                payload.copyMode = "entire";
            } else if (copyState.categories) {
                payload.copyMode = "categories";
                payload.selectedCategories = selectedCategories;
            } else if (copyState.items) {
                payload.copyMode = "items";
                payload.selectedItems = selectedItems;
            }
            
            
            const response = await manageStores.copyMenuToBranch(payload);
            
            console.log('Copy response:', response);
            
            // Show success message with statistics
            let successMessage = response.message || "Menu copied successfully!";
            if (response.totalCopied) {
                const stats = [];
                if (response.totalCopied.menus) stats.push(`${response.totalCopied.menus} menu(s)`);
                if (response.totalCopied.categories) stats.push(`${response.totalCopied.categories} categories`);
                if (response.totalCopied.menuItems) stats.push(`${response.totalCopied.menuItems} items`);
                
                if (stats.length > 0) {
                    successMessage += ` Copied: ${stats.join(', ')}.`;
                }
            }
            
            Toast({
                type: "success",
                message: successMessage
            });
            
            // Reset form and close modal
            // Reset all state variables
            setSelectedSourceStore(null);
            setSelectedTargetStore(null);
            setSelectedCategories([]);
            setSelectedItems([]);
            setCopyState({ entire: true, categories: false, items: false });
            setStores([]);
            setCategories([]);
            setMenuItems({});
            
            onClose();
            
        } catch (error) {
            console.error('Error copying menu:', error);
            Toast({
                type: "error",
                message: error.message || "Failed to copy menu. Please try again."
            });
        } finally {
            setLoading(false);
        }
    };
    
    const toggleCategoryDropdown = (index) => {
        setActiveDropdown(activeDropdown === index ? null : index);
    };
    

    
    const getWhatToCopyText = () => {
        if (copyState.entire) return "Entire Menu";
        if (copyState.categories) {
            if (selectedCategories.length === 0) return "No categories selected";
            const categoryNames = categories
                .filter(cat => selectedCategories.includes(cat._id))
                .map(cat => cat.name)
                .join(", ");
            return `Categories: ${categoryNames}`;
        }
        if (copyState.items) {
            if (selectedItems.length === 0) return "No items selected";
            return `${selectedItems.length} selected items`;
        }
        return "Not specified";
    };

    return (
        <>
            <CopySoreWrap>
                <strong className="heading">
                    The selected branch's menu will be fully replaced and offers that are currently applied will be
                    deleted.
                </strong>
                <Form form={form} onSubmit={() => handleSubmit()}>
                    <Form.Item
                        label="Select Source Store"
                        placeholder={storesLoading ? "Loading stores..." : "Select Source Store"}
                        type="select"
                        rounded
                        name="sourceStore"
                        lg
                        options={stores}
                        disabled={storesLoading}
                        value={selectedSourceStore || undefined}
                        onChange={handleSourceStoreChange}
                        showSearch={false}
                        $bgWhite>
                        <Field />
                    </Form.Item>
                    <Form.Item
                        label="Select 'Copy to' store"
                        placeholder={storesLoading ? "Loading stores..." : "Select 'Copy to' store"}
                        type="select"
                        rounded
                        name="copyToStore"
                        lg
                        options={stores.filter(store => store.value !== selectedSourceStore)}
                        disabled={storesLoading || !selectedSourceStore}
                        value={selectedTargetStore || undefined}
                        onChange={handleTargetStoreChange}
                        showSearch={false}
                        $bgWhite>
                        <Field />
                    </Form.Item>
                    <h2 className="title">Select "Copy to" option</h2>

                    <div className="options-wrap">
                        <Form.Item
                            label="Copy Entire Menu"
                            type="radio"
                            rounded
                            name="copyEntireMenu"
                            value={copyState?.entire}
                            onChange={(checked, e) => {
                                console.log('Entire menu radio clicked, checked:', checked, 'event:', e);
                                if (checked) handleCopyModeChange('entire');
                            }}
                            lg
                            $bgWhite
                            // checked={copyState?.entire}
                            >
                            <Field />
                        </Form.Item>
                        <Form.Item
                            label="Copy Selected Categories"
                            type="radio"
                            rounded
                            name="copySelectedCategories"
                            onChange={(checked, e) => {
                                if (checked) handleCopyModeChange('categories');
                            }}
                            lg
                            value={copyState?.categories}
                            disabled={true}
                            $bgWhite>
                            <Field />
                        </Form.Item>
                        <Form.Item
                            label="Copy Selected Items"
                            type="radio"
                            rounded
                            name="copySelectedItems"
                            value={copyState?.items}
                            onChange={(checked, e) => {
                                if (checked) handleCopyModeChange('items')
                            }}
                            lg
                            disabled={true}
                            $bgWhite>
                            <Field />
                        </Form.Item>
                    </div>

                    {copyState?.categories && (
                        <div className="categories-wrap">
                            {categoriesLoading ? (
                                <p>Loading categories...</p>
                            ) : categories.length > 0 ? (
                                categories.map((category) => (
                                    <Form.Item 
                                        key={category._id}
                                        label={category.name} 
                                        type="checkbox" 
                                        rounded 
                                        name={`category_${category._id}`}
                                        onChange={(checked) => handleCategorySelection(category._id, checked)}
                                        lg 
                                        $bgWhite 
                                        noMargin>
                                        <Field />
                                    </Form.Item>
                                ))
                            ) : (
                                <p>No categories available for the selected store.</p>
                            )}
                        </div>
                    )}

                    {copyState?.items && (
                        <div className="items-wrap">
                            {categoriesLoading ? (
                                <p>Loading menu items...</p>
                            ) : categories.length > 0 ? (
                                categories.map((category, index) => {
                                    const categoryItems = menuItems[category._id] || [];
                                    return (
                                        <div key={category._id} className={`item ${activeDropdown === index ? "active" : ""}`}>
                                            <div className="head" onClick={() => toggleCategoryDropdown(index)}>
                                                <img src={arrowIcon} alt="arrowIcon" className="arrowIcon" />
                                                <strong className="head-title">
                                                    {category.name} ({categoryItems.length} items)
                                                </strong>
                                            </div>
                                            <div className="sub-items">
                                                {categoryItems.length > 0 ? (
                                                    categoryItems.map((item) => (
                                                        <Form.Item
                                                            key={item._id}
                                                            label={`${item.name} - $${item.price || '0.00'}`}
                                                            type="checkbox"
                                                            rounded
                                                            name={`item_${item._id}`}
                                                            onChange={(checked) => handleItemSelection(item._id, checked)}
                                                            lg
                                                            $bgWhite
                                                            noMargin>
                                                            <Field />
                                                        </Form.Item>
                                                    ))
                                                ) : (
                                                    <p>No items available in this category.</p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <p>No categories available for the selected store.</p>
                            )}
                        </div>
                    )}

                    <div className="separator"></div>

                    <h2 className="title">Summary of Copy Action</h2>

                    <ul className="details">
                        <li>
                            <span className="name">Source Store:</span>
                            <span className="value">{selectedSourceStore?.label || 'Not Selected'}</span>
                        </li>
                        <li>
                            <span className="name">Copy to Store:</span>
                            <span className="value">{selectedTargetStore?.label || 'Not Selected'}</span>
                        </li>
                        <li>
                            <span className="name">What to copy:</span>
                            <span className="value">{getWhatToCopyText()}</span>
                        </li>
                    </ul>

                    <div className="btn-holder">
                        <Button 
                            lg 
                            type="submit"
                            disabled={loading || storesLoading || !selectedSourceStore || !selectedTargetStore}
                            loading={loading}>
                            {loading ? "Copying..." : "Confirm Copy"}
                        </Button>
                    </div>
                </Form>
            </CopySoreWrap>
        </>
    );
};

export default CopyStore;
